import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

type Props = {
  primaryLabel: string
  primaryDisabled?: boolean
  primaryDisabledTitle?: string
  menuDisabled?: boolean
  menuAriaLabel?: string
  onPrimaryClick: () => void
  menu: ReactNode
  menuOpen?: boolean
  onMenuOpenChange?: (open: boolean) => void
}

export default function JournalEntryPostSplitButton({
  primaryLabel,
  primaryDisabled = false,
  primaryDisabledTitle,
  menuDisabled = false,
  menuAriaLabel = 'Journal entry actions',
  onPrimaryClick,
  menu,
  menuOpen: menuOpenProp,
  onMenuOpenChange,
}: Props) {
  const [menuOpenInternal, setMenuOpenInternal] = useState(false)
  const menuOpen = menuOpenProp ?? menuOpenInternal
  const setMenuOpen = onMenuOpenChange ?? setMenuOpenInternal
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return
      setMenuOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen, setMenuOpen])

  useEffect(() => {
    if (menuDisabled) setMenuOpen(false)
  }, [menuDisabled, setMenuOpen])

  const splitDisabled = primaryDisabled && menuDisabled

  return (
    <div className="relative inline-flex" ref={containerRef}>
      <div
        className={[
          'inline-flex h-9 items-stretch overflow-hidden rounded-md text-xs font-semibold text-white shadow-sm',
          '[background:var(--gradient-primary)]',
          splitDisabled ? 'cursor-not-allowed opacity-50' : '',
        ].join(' ')}
      >
        <button
          type="button"
          onClick={onPrimaryClick}
          disabled={primaryDisabled}
          title={primaryDisabled ? primaryDisabledTitle : undefined}
          className="inline-flex items-center px-3 hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            if (menuDisabled) return
            setMenuOpen(!menuOpen)
          }}
          disabled={menuDisabled}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={menuAriaLabel}
          className="inline-flex items-center border-l border-white/25 px-2 hover:bg-black/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </div>
      {menuOpen ? (
        <div role="menu" className="je-posted-badge__menu">
          {menu}
        </div>
      ) : null}
    </div>
  )
}
